import { useEffect, useState, useCallback, Suspense } from "react";
import { useNavigate, useSearchParams, Outlet } from 'react-router-dom';
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Header from "../../components/navigationHeader/Header";
import LandingPageWrapper from "../../components/LandingPageWrapper";
import ChangeDomainModal from "../../routes/domain/modals/ChangeDomainModal";
import { useAppContext } from "../../common/ApplicationServicesContext";
import User from "../../services/user";
import Product from "../../services/product";
import TermsAndConditionsModal from "../../components/controls/TermsAndConditionsModal";
import TrainingModal from "../../components/controls/TrainingModal";
import PubSub from "../../services/PubSub";
import Domain from "../../services/domain";

const Home = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const redirect = searchParams.get("redirect");

    const [translate] = useTranslation();
    const {
        user,
        setUser,
        refreshUser,
        setIsRootDomain,
        domain: {
            _id: domainId,
            acceptedTermsAndConditionsUrl,
            termsAndConditionsUrl,
            trainingUrls
        },
        setDomain,
        handleLogout
    } = useAppContext();
    const [hasDomain, setHasDomain] = useState(!!domainId);
    const [initialized, setInitialized] = useState(false);
    const [userHasNoPermissions, setUserHasNoPermissions] = useState(true);

    const [showTC, setShowTC] = useState(false);
    const [showTraining, setShowTraining] = useState(false);

    const checkPermissions = (userObject) => userObject.appAdmin || userObject.adminForDomains.length > 0 || userObject.uiPermissions.length > 0;

    const initialize = useCallback(async (user) => {
        if (user._id) {
            setInitialized(true);
            setUserHasNoPermissions(!checkPermissions(user));
            return;
        }

        const userDetails = await User.getUser();
        if (!userDetails.error) {
            const loggedUser = userDetails.data;

            const hasPermissions = checkPermissions(loggedUser);
            setUserHasNoPermissions(!hasPermissions);

            if (hasPermissions) {
                if (loggedUser.singleDomainUser) {
                    const promptInfoResponse = await Product.getDomainPromptInfo(loggedUser.domain[0]._id);
                    const { helpUrl, termsAndConditionsUrl } = promptInfoResponse.data;
                    if (loggedUser.domain[0].enabled) {
                        setDomain({
                            _id: loggedUser.domain[0]._id,
                            name: loggedUser.domain[0].name,
                            helpUrl,
                            termsAndConditionsUrl,
                            enabled: loggedUser.domain[0].enabled,
                            trainingUrls: loggedUser.domain[0].trainingUrls ?? [],
                            trainingText: loggedUser.domain[0].trainingText,
                            acceptedTermsAndConditionsUrl: loggedUser.domain[0].acceptedTermsAndConditionsUrl,
                        });
                        if (redirect) {
                            navigate(redirect);
                        }
                    } else {
                        setHasDomain(false);
                    }
                } else {
                    setHasDomain(false);
                }
                setIsRootDomain(loggedUser.domain[0].rootDomain || false);
                setUser(loggedUser);
            }

            setInitialized(true);
        }
    }, [setDomain, setUser, setIsRootDomain, setInitialized, setHasDomain, redirect, navigate]);

    useEffect(() => {
        if (!initialized) {
            initialize(user);
        }
    }, [initialize, initialized, user]);

    useEffect(() => {
        if (domainId) {
            setHasDomain(true);
        }
    }, [domainId, setHasDomain]);

    useEffect(() => {
        if (user.singleDomainUser && !acceptedTermsAndConditionsUrl && user.adminForDomains.includes(domainId) && termsAndConditionsUrl) {
            setShowTC(true);
        }
    }, [user, acceptedTermsAndConditionsUrl, termsAndConditionsUrl, domainId]);

    useEffect(() => {
        if (user.singleDomainUser && trainingUrls?.length > 0) {
            const hasCompleted = user.trainingsCompleted?.some(
                (t) => String(t.trainingId) === String(trainingUrls[trainingUrls.length - 1]._id)
            );
            if (!hasCompleted) {
                setShowTraining(true);
            }
        }
    }, [user, trainingUrls]);

    useEffect(() => {
        const id = PubSub.subscribe("show-training-modal", async () => {
            const response = await Domain.getDomain(domainId);
            if (response && !response.error) {
                setDomain({
                    ...response.data,
                    termsAndConditionsUrl
                });
            }
            setShowTraining(true);
        });
        return () => PubSub.unsubscribe("show-training-modal", id);
    }, [termsAndConditionsUrl, setDomain, domainId]);

    if (!initialized) {
        return <div className="bg-white h-100"></div>
    }

    if (userHasNoPermissions) {
        return <LandingPageWrapper>
            <div className="d-flex flex-column text-center">
                <span>{translate('OTM_USER_HAS_NO_PERMISSIONS')}</span>
                <div>
                    <Button
                        variant="primary"
                        className="mt-2"
                        onClick={() => handleLogout(navigate)}
                    >
                        {translate('OTM_LOGOUT')}
                    </Button>
                </div>
            </div>
        </LandingPageWrapper>
    }

    if (!hasDomain) {
        return <ChangeDomainModal show={true} cancelText="OTM_LOGOUT" onCancel={() => handleLogout(navigate)} redirect={redirect} />
    }

    return <>
        <TermsAndConditionsModal
            show={showTC}
            onConfirm={() => { setShowTC(false) }}
        />
        <TrainingModal
            show={showTraining}
            onConfirm={async () => {
                await refreshUser();
                setShowTraining(false);
            }}
        />
        <Header />
        <main className="container-fluid flex-grow-1 d-flex flex-column overflow-auto bg-light">
            <Suspense fallback={<div className="bg-white h-100"></div>}>
                <Outlet context={{ domainId }} />
            </Suspense>
        </main>
    </>
};

export default Home;
